import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShoppingBag, Heart, Minus, Plus, ClipboardList, X, Store, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../api/axios';
import useCartStore from '../store/cartStore';
import useAuthStore from '../store/authStore';
import useWishlistStore from '../store/wishlistStore';
import { formatDeliveryLong } from '../utils/deliveryTime';
import useNotepadStore from '../store/notepadStore';
import AddToNotepadDialog from '../components/AddToNotepadDialog';
import { toast } from 'sonner';
import { imgVariant } from '../utils/imgUrl';
import { formatEUR } from '../utils/format';

// Bullet style symbols mapping
const BULLET_SYMBOLS = {
  'square': '▪',
  'square-outline': '▫',
  'diamond': '◆',
  'diamond-outline': '◇',
  'circle': '●',
  'circle-outline': '○',
  'check': '✓',
  'arrow': '→',
  'star': '★',
  'heart': '♥',
};

// Helper function to format description with nice bullet points
const formatDescription = (text, bulletStyle = { style: 'square', color: '#C4A084' }) => {
  if (!text) return null;
  
  const symbol = BULLET_SYMBOLS[bulletStyle.style] || '▪';
  const color = bulletStyle.color || '#C4A084';
  
  const lines = text.split('\n');
  const elements = [];
  let currentList = [];
  let listIndex = 0;
  
  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${listIndex}`} className="space-y-2 my-3">
          {currentList.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-gray-600">
              <span style={{ color: color }} className="mt-1 flex-shrink-0">{symbol}</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
      currentList = [];
      listIndex++;
    }
  };
  
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    // Check if line starts with bullet markers
    if (trimmed.match(/^[-•*]\s+/)) {
      const content = trimmed.replace(/^[-•*]\s+/, '');
      currentList.push(content);
    } else if (trimmed === '') {
      flushList();
      // Add spacing for empty lines
      elements.push(<div key={`space-${idx}`} className="h-2" />);
    } else {
      flushList();
      elements.push(
        <p key={`p-${idx}`} className="text-gray-600">
          {trimmed}
        </p>
      );
    }
  });
  
  flushList();
  return elements;
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [recoTagline, setRecoTagline] = useState('Auch interessant für dich');
  const [pairProduct, setPairProduct] = useState(null);
  const [pairTagline, setPairTagline] = useState('');
  const [fbtProducts, setFbtProducts] = useState([]);
  const [fbtTagline, setFbtTagline] = useState('');
  const [fbtSource, setFbtSource] = useState('');
  const [bulletStyle, setBulletStyle] = useState({ style: 'square', color: '#C4A084' });
  const [giftLabelText, setGiftLabelText] = useState('');
  const [giftNote, setGiftNote] = useState('');
  const addItem = useCartStore(state => state.addItem);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const fetchWishlist = useWishlistStore(state => state.fetchWishlist);
  const toggleWishlist = useWishlistStore(state => state.toggleWishlist);
  const wishlistIds = useWishlistStore(state => state.wishlistIds);
  const fetchNotepad = useNotepadStore(state => state.fetchNotepad);
  const addToNotepad = useNotepadStore(state => state.addToNotepad);
  const notepadItems = useNotepadStore(state => state.notepadItems);
  
  const [notepadDialog, setNotepadDialog] = useState({ open: false, product: null });
  
  // Helper function to check if product is in wishlist
  const isInWishlist = (productId) => wishlistIds.includes(productId);
  
  // Helper function to check if product is in notepad
  const isInNotepad = (productId) => notepadItems.some(item => item.product_id === productId);

  // Load bullet style setting
  useEffect(() => {
    const loadBulletStyle = async () => {
      try {
        const response = await axiosInstance.get('/settings/bullet-style');
        setBulletStyle(response.data);
      } catch (error) {
        console.error('Error loading bullet style:', error);
      }
    };
    loadBulletStyle();
  }, []);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  // SEO: inject <title>, <meta description>, and schema.org JSON-LD when product loads
  useEffect(() => {
    if (!product) return;
    const metaTitle = product.meta_title || `${product.name} · Homeyoursoul`;
    const metaDesc = (product.meta_description
      || (product.description || '').replace(/\s+/g, ' ').slice(0, 160)
      || 'Handgemachte Lieblingsstücke und kuratierte Mystery Boxen bei Homeyoursoul.');
    document.title = metaTitle;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', metaDesc);
    setMeta('og:title', metaTitle);
    setMeta('og:description', metaDesc);
    if (product.images && product.images[0]) {
      setMeta('og:image', product.images[0].startsWith('http') ? product.images[0] : window.location.origin + product.images[0]);
    }

    // Inject schema.org JSON-LD
    const SCHEMA_ID = 'product-jsonld';
    let scriptEl = document.getElementById(SCHEMA_ID);
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = SCHEMA_ID;
      scriptEl.type = 'application/ld+json';
      document.head.appendChild(scriptEl);
    }
    axiosInstance.get(`/products/${product.id}/schema-jsonld`).then((r) => {
      if (scriptEl) scriptEl.textContent = JSON.stringify(r.data);
    }).catch(() => {});

    return () => {
      // cleanup schema tag on unmount (title/meta are overwritten by next page)
      const el = document.getElementById(SCHEMA_ID);
      if (el) el.remove();
    };
  }, [product]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWishlist();
      fetchNotepad();
    }
  }, [isAuthenticated, fetchWishlist, fetchNotepad]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/products/${id}?include_vendor_info=true`);
      setProduct(response.data);
      // Set first color as default if available
      if (response.data.color_variants && response.data.color_variants.length > 0) {
        setSelectedColor(response.data.color_variants[0]);
      }
      // Fetch AI-curated recommendations
      fetchAiRecommendations(response.data.id);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Produkt nicht gefunden');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };


  const fetchAiRecommendations = async (productId) => {
    try {
      const response = await axiosInstance.get(`/recommendations/for-product/${productId}`, {
        params: { limit: 6 },
      });
      setRelatedProducts(response.data?.products || []);
      setRecoTagline(response.data?.tagline || 'Auch interessant für dich');
    } catch (error) {
      console.error('Error fetching AI recommendations:', error);
    }
    // Frequently Bought Together (Collaborative Filtering aus Order-Daten)
    try {
      const res = await axiosInstance.get(
        `/recommendations/frequently-bought-together/${productId}`,
        { params: { limit: 4 } },
      );
      setFbtProducts(res.data?.products || []);
      setFbtTagline(res.data?.tagline || '');
      setFbtSource(res.data?.source || '');
    } catch (e) { /* still-launching shop → keine Order-Historie, OK */ }
    // Pair-With (LLM-kuratiertes Gift-Duo)
    try {
      const res = await axiosInstance.get(`/recommendations/pair-with/${productId}`);
      const first = (res.data?.products || [])[0];
      if (first) {
        setPairProduct(first);
        setPairTagline(res.data?.tagline || 'Perfektes Pärchen');
      } else {
        setPairProduct(null);
      }
    } catch (e) { setPairProduct(null); }
  };

  const handleAddToCart = () => {
    if (product) {
      // Check if color is required but not selected
      if (product.color_variants && product.color_variants.length > 0 && !selectedColor) {
        toast.error('Bitte wählen Sie eine Farbe');
        return;
      }
      
      // Calculate final price with color adjustment + sale_price honored
      const basePrice = (product.sale_price && product.sale_price < product.price)
        ? product.sale_price
        : product.price;
      const finalPrice = selectedColor
        ? basePrice + selectedColor.price_adjustment
        : basePrice;
      
      const personalisation = (giftLabelText.trim() || giftNote.trim())
        ? {
            gift_label_text: giftLabelText.trim().slice(0, 30) || null,
            gift_note: giftNote.trim().slice(0, 200) || null,
          }
        : null;

      if (!addItem(product, quantity, selectedColor, finalPrice, personalisation)) return;
      const suffix = personalisation?.gift_label_text
        ? ` mit Etikett-Botschaft „${personalisation.gift_label_text}"`
        : '';
      toast.success(`${quantity}x ${product.name}${selectedColor ? ` (${selectedColor.color_name})` : ''}${suffix} wurde zum Warenkorb hinzugefügt`);
      setGiftLabelText('');
      setGiftNote('');
    }
  };

  const getAvailableStock = () => {
    if (selectedColor) {
      return selectedColor.stock;
    }
    return product?.stock || 0;
  };

  const getFinalPrice = () => {
    if (!product) return 0;
    const basePrice = (product.sale_price && product.sale_price < product.price)
      ? product.sale_price
      : product.price;
    if (selectedColor) {
      return basePrice + selectedColor.price_adjustment;
    }
    return basePrice;
  };

  const handleAddToWishlist = async () => {
    // Wishlist unterstützt Gäste (LocalStorage) UND eingeloggte Nutzer (Backend).
    // Kein Login-Redirect mehr, damit Besucher Lieblingsstücke ohne Konto merken können.
    const wasInWishlist = isInWishlist(id);
    const success = await toggleWishlist(id);

    if (success) {
      if (wasInWishlist) {
        toast.success('Von Favoritenliste entfernt');
      } else {
        toast.success('Zur Favoritenliste hinzugefügt');
      }
    } else {
      toast.error('Fehler beim Aktualisieren der Favoritenliste');
    }
  };

  const handleOpenNotepadDialog = () => {
    if (!isAuthenticated) {
      toast.error('Bitte melden Sie sich an');
      navigate('/login');
      return;
    }

    setNotepadDialog({ open: true, product });
  };

  const handleAddToNotepadConfirm = async (quantity) => {
    const success = await addToNotepad(product.id, quantity);
    
    if (success) {
      toast.success(`${quantity}x ${product.name} zum Merkzettel hinzugefügt`);
    } else {
      toast.error('Fehler beim Hinzufügen zum Merkzettel');
    }
    
    setNotepadDialog({ open: false, product: null });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F1E8' }}>
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5EDE4' }}>
      {/* Back button — visible on all viewports */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 pt-3 md:pt-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-[#5D4E3C] hover:text-[#C4A084] transition-colors py-2"
          aria-label="Zurück"
          data-testid="product-detail-back"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Zurück</span>
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 pb-6 md:py-4">
        {/* MAIN CARD — image left, key info top-right */}
        <div className="bg-white border" style={{ borderColor: '#E8DCC8' }} data-testid="product-detail-card">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 p-4 sm:p-6 lg:p-10">
            {/* LEFT: image — soft border for separation from the cream background */}
            <div>
              <div className="aspect-square overflow-hidden bg-[#FAF5EE] mb-3 border" style={{ borderColor: '#E8DCC8' }}>
                {(selectedImage === -1 && selectedColor?.image) ? (
                  <img
                    src={imgVariant(selectedColor.image, { w: 900 })}
                    alt={`${product.name} - ${selectedColor.color_name}`}
                    className="w-full h-full object-cover copy-protect-image"
                    data-testid="product-variant-image"
                    draggable="false"
                  />
                ) : product.images && product.images.length > 0 ? (
                  <img
                    src={imgVariant(product.images[selectedImage >= 0 ? selectedImage : 0], { w: 900 })}
                    alt={product.name}
                    className="w-full h-full object-cover copy-protect-image"
                    data-testid="product-main-image"
                    draggable="false"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ShoppingBag className="h-24 w-24 text-[#C4A084]/40" />
                  </div>
                )}
              </div>
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className="aspect-square overflow-hidden"
                      style={{
                        border: `2px solid ${selectedImage === index ? '#C4A084' : '#E8DCC8'}`,
                      }}
                    >
                      <img src={imgVariant(image, { w: 160 })} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover copy-protect-image" draggable="false" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* RIGHT: ONLY essentials (title, vendor, price, color variants, qty, stock) */}
            <div className="flex flex-col">
              {product.category && (
                <p className="text-[10px] tracking-[3px] uppercase mb-2" style={{ color: '#C4A084' }}>
                  {product.category}
                </p>
              )}
              <h1
                className="text-2xl sm:text-3xl font-bold text-[#5D4E3C] leading-tight"
                data-testid="product-name"
                style={{ fontFamily: 'Georgia, serif' }}
              >
                {product.name}
              </h1>
              <div className="w-10 h-[2px] my-3" style={{ backgroundColor: '#C4A084' }} />

              {product.vendor_id && product.vendor_shop_name && (
                <Link
                  to={`/shop/${product.vendor_id}`}
                  className="inline-flex items-center gap-2 mb-3 text-xs text-[#8B7355] hover:text-[#5D4E3C]"
                >
                  <Store className="h-3.5 w-3.5" />
                  <span>Verkauft von <strong>{product.vendor_shop_name}</strong></span>
                </Link>
              )}

              {product.is_handmade && (
                <span className="inline-block self-start mb-4 text-[11px] tracking-wide bg-[#FAF5EE] text-[#8B7355] px-2.5 py-0.5 border border-[#E8DCC8]">
                  ♥ Handgemacht
                </span>
              )}

              {/* Price */}
              <div data-testid="product-price">
                {product.sale_price && product.sale_price < product.price ? (
                  <div className="flex items-baseline flex-wrap gap-2">
                    <span
                      className="inline-block px-3 py-1.5 text-xl sm:text-2xl font-bold text-white"
                      style={{ backgroundColor: '#B0512E' }}
                    >
                      {formatEUR((product.sale_price + ((selectedColor?.price_adjustment) || 0)))}
                      {product.price_type === 'per_quantity' && product.price_quantity > 1 && (
                        <span className="text-xs font-normal ml-1.5 opacity-90">/ {product.price_quantity} Stück</span>
                      )}
                    </span>
                    <span className="text-base sm:text-lg text-[#8B7355] line-through">
                      {formatEUR(product.price)}
                    </span>
                    <span className="px-2 py-1 text-xs font-bold text-white bg-[#B0512E]">
                      −{Math.round((1 - product.sale_price / product.price) * 100)}%
                    </span>
                  </div>
                ) : (
                  <span
                    className="inline-block px-3 py-1.5 text-xl sm:text-2xl font-bold text-white"
                    style={{ backgroundColor: '#C4A084' }}
                  >
                    {formatEUR(getFinalPrice())}
                    {product.price_type === 'per_quantity' && product.price_quantity > 1 && (
                      <span className="text-xs font-normal ml-1.5 opacity-90">/ {product.price_quantity} Stück</span>
                    )}
                  </span>
                )}
                {selectedColor && selectedColor.price_adjustment !== 0 && (
                  <span className="text-xs text-[#8B7355] ml-2 block mt-1">
                    Basispreis {formatEUR(product.price)}
                  </span>
                )}
                <p className="text-[11px] text-[#8B7355] mt-1">
                  Kein MwSt-Ausweis (§ 19 UStG) · zzgl. Versand · 🇩🇪 nur Deutschland
                </p>
              </div>

              {/* Color variants */}
              {product.color_variants && product.color_variants.length > 0 && (
                <div className="mt-5">
                  <p className="text-[10px] tracking-[3px] uppercase font-semibold mb-2" style={{ color: '#5D4E3C' }}>
                    Farbe wählen
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.color_variants.map((variant, index) => {
                      const isActive = selectedColor?.color_name === variant.color_name;
                      const oos = !(variant.stock > 0);
                      return (
                        <button
                          key={index}
                          onClick={() => {
                            if (oos) return;
                            setSelectedColor(variant);
                            if (variant.image) setSelectedImage(-1);
                          }}
                          disabled={oos}
                          className="relative flex flex-col items-center gap-1 p-1.5 transition-all"
                          style={{
                            border: `2px solid ${isActive ? '#5D4E3C' : '#E8DCC8'}`,
                            backgroundColor: isActive ? '#FAF5EE' : '#FFFFFF',
                            opacity: oos ? 0.45 : 1,
                            cursor: oos ? 'not-allowed' : 'pointer',
                          }}
                          data-testid={`color-variant-${variant.color_name}`}
                          title={variant.color_name}
                        >
                          {variant.image ? (
                            <div className="w-10 h-10 sm:w-12 sm:h-12 overflow-hidden">
                              <img src={imgVariant(variant.image, { w: 160 })} alt={variant.color_name} className="w-full h-full object-cover" />
                            </div>
                          ) : variant.color_code ? (
                            <div
                              className="w-7 h-7 sm:w-8 sm:h-8 border"
                              style={{ backgroundColor: variant.color_code, borderColor: '#E8DCC8' }}
                            />
                          ) : null}
                          <span className={`text-[10px] leading-tight ${oos ? 'line-through' : ''} text-[#5D4E3C]`}>
                            {variant.color_name}
                          </span>
                          {variant.price_adjustment !== 0 && (
                            <span className="text-[9px] text-[#A89A85]">
                              {variant.price_adjustment > 0 ? '+' : ''}{formatEUR(variant.price_adjustment)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {selectedColor && (
                    <p className="text-[11px] text-[#8B7355] mt-2">
                      Verfügbar: {selectedColor.stock}
                    </p>
                  )}
                </div>
              )}

              {/* Quantity */}
              <div className="mt-5">
                <p className="text-[10px] tracking-[3px] uppercase font-semibold mb-2" style={{ color: '#5D4E3C' }}>
                  Menge
                </p>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    className="w-9 h-9 flex items-center justify-center border disabled:opacity-30"
                    style={{ borderColor: '#E8DCC8', color: '#5D4E3C' }}
                    data-testid="decrease-quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="text-base font-semibold w-8 text-center text-[#5D4E3C]" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.min(getAvailableStock(), quantity + 1))}
                    disabled={quantity >= getAvailableStock()}
                    className="w-9 h-9 flex items-center justify-center border disabled:opacity-30"
                    style={{ borderColor: '#E8DCC8', color: '#5D4E3C' }}
                    data-testid="increase-quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <span className="text-xs ml-3" style={{ color: (getAvailableStock() > 0 || product.delivery_time === '3-7') ? '#5D8A5A' : '#B85C4C' }}>
                    {getAvailableStock() > 0
                      ? `${getAvailableStock()} verfügbar · Lieferzeit ${formatDeliveryLong(product)}`
                      : product.delivery_time === '3-7'
                        ? `Auf Bestellung · ${formatDeliveryLong(product)}`
                        : 'Nicht auf Lager'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Personalisierung — nur bei Limited-Box-Produkten */}
          {product.collection_slug && (
            <div
              className="mx-4 sm:mx-6 lg:mx-10 mt-4 p-4 border border-dashed bg-white"
              style={{ borderColor: '#C4A084' }}
              data-testid="gift-personalisation-box"
            >
              <p className="text-[10px] tracking-[3px] uppercase font-semibold mb-1" style={{ color: '#C4A084' }}>
                ✨ Personalisierte Geschenkbox
              </p>
              <p className="text-xs text-[#5D4E3C] mb-3">
                Optional: Empfänger-Name oder kurze Botschaft für das Etikett (z.B. „Für Anna" oder „Frohe Weihnachten")
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="gift-label-text" className="text-[11px] text-[#5D4E3C] flex items-center justify-between mb-1">
                    <span>♡ Etikett-Botschaft <em className="text-[#9C8C7A] not-italic">(erscheint auf dem Etikett)</em></span>
                    <span className={`text-[10px] ${giftLabelText.length > 30 ? 'text-red-600' : 'text-[#9C8C7A]'}`}>
                      {giftLabelText.length}/30
                    </span>
                  </label>
                  <input
                    id="gift-label-text"
                    type="text"
                    maxLength={30}
                    value={giftLabelText}
                    onChange={(e) => setGiftLabelText(e.target.value)}
                    placeholder="z.B. Für Anna"
                    className="w-full text-sm px-2.5 py-2 border border-[#D4C4B0] focus:outline-none focus:border-[#5D4E3C] bg-[#FAF5EE]"
                    data-testid="gift-label-input"
                  />
                </div>
                <div>
                  <label htmlFor="gift-note" className="text-[11px] text-[#5D4E3C] flex items-center justify-between mb-1">
                    <span>📝 Allgemeine Bemerkung <em className="text-[#9C8C7A] not-italic">(nur intern, nicht auf Etikett)</em></span>
                    <span className={`text-[10px] ${giftNote.length > 200 ? 'text-red-600' : 'text-[#9C8C7A]'}`}>
                      {giftNote.length}/200
                    </span>
                  </label>
                  <input
                    id="gift-note"
                    type="text"
                    maxLength={200}
                    value={giftNote}
                    onChange={(e) => setGiftNote(e.target.value)}
                    placeholder="z.B. Geburtstag, bitte separat verpacken"
                    className="w-full text-sm px-2.5 py-2 border border-[#D4C4B0] focus:outline-none focus:border-[#5D4E3C] bg-[#FAF5EE]"
                    data-testid="gift-note-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Trennlinie + Actionleiste */}
          <div
            className="px-4 sm:px-6 lg:px-10 py-3 sm:py-4 border-t grid grid-cols-3 gap-2 sm:flex sm:items-center sm:justify-end sm:gap-2"
            style={{ borderColor: '#E8DCC8', backgroundColor: '#FAF5EE' }}
          >
            <Button
              variant="outline"
              onClick={handleOpenNotepadDialog}
              data-testid="add-to-notepad-button"
              className="border-[#C4A084] text-[#5D4E3C] hover:bg-white px-2 sm:px-4"
            >
              <ClipboardList className={`h-4 w-4 sm:mr-1.5 ${isInNotepad(id) ? 'text-blue-500' : 'text-[#C4A084]'}`} />
              <span className="hidden sm:inline">Merkzettel</span>
              <span className="sm:hidden text-[11px] ml-1">Merken</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleAddToWishlist}
              data-testid="add-to-wishlist-button"
              className="border-[#C4A084] text-[#5D4E3C] hover:bg-white px-2 sm:px-4"
            >
              <Heart className={`h-4 w-4 sm:mr-1.5 ${isInWishlist(id) ? 'fill-pink-500 text-pink-500' : 'text-[#C4A084]'}`} />
              <span className="hidden sm:inline">Favorit</span>
              <span className="sm:hidden text-[11px] ml-1">Favorit</span>
            </Button>
            <Button
              onClick={handleAddToCart}
              disabled={!(getAvailableStock() > 0)}
              className="text-white hover:opacity-90 px-2 sm:px-4"
              style={{ backgroundColor: '#5D4E3C' }}
              data-testid="add-to-cart-button"
            >
              <ShoppingBag className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">{getAvailableStock() > 0 ? 'In den Warenkorb' : 'Ausverkauft'}</span>
              <span className="sm:hidden text-[11px] ml-1">{getAvailableStock() > 0 ? 'Warenkorb' : 'Ausverkauft'}</span>
            </Button>
          </div>
        </div>

        {/* SECONDARY CARD — beschreibung + technische Details */}
        <div className="bg-white border mt-4 sm:mt-6 p-4 sm:p-6 lg:p-10 grid grid-cols-1 md:grid-cols-3 gap-6" style={{ borderColor: '#E8DCC8' }}>
          <div className="md:col-span-2">
            <p className="text-[10px] tracking-[3px] uppercase font-semibold mb-2" style={{ color: '#C4A084' }}>
              Produktbeschreibung
            </p>
            <div className="text-sm sm:text-base text-[#3D3D3D] leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              {formatDescription(product.description, bulletStyle)}
            </div>
          </div>
          <div>
            <p className="text-[10px] tracking-[3px] uppercase font-semibold mb-3" style={{ color: '#C4A084' }}>
              Details
            </p>
            <dl className="text-sm space-y-2">
              {product.category && (
                <div className="flex justify-between gap-3 border-b pb-2" style={{ borderColor: '#F0E5D2' }}>
                  <dt className="text-[#8B7355]">Kategorie</dt>
                  <dd className="text-[#3D3D3D] text-right">{product.category}</dd>
                </div>
              )}
              {product.subcategory && (
                <div className="flex justify-between gap-3 border-b pb-2" style={{ borderColor: '#F0E5D2' }}>
                  <dt className="text-[#8B7355]">Subkategorie</dt>
                  <dd className="text-[#3D3D3D] text-right">{product.subcategory}</dd>
                </div>
              )}
              {product.sku && (
                <div className="flex justify-between gap-3 border-b pb-2" style={{ borderColor: '#F0E5D2' }}>
                  <dt className="text-[#8B7355]">Artikel-Nr.</dt>
                  <dd className="text-[#3D3D3D] text-right font-mono text-xs">{product.sku}</dd>
                </div>
              )}
              {product.weight && (
                <div className="flex justify-between gap-3 border-b pb-2" style={{ borderColor: '#F0E5D2' }}>
                  <dt className="text-[#8B7355]">Gewicht</dt>
                  <dd className="text-[#3D3D3D] text-right">{product.weight} g</dd>
                </div>
              )}
              {(product.length || product.width || product.height) && (
                <div className="flex justify-between gap-3 border-b pb-2" style={{ borderColor: '#F0E5D2' }}>
                  <dt className="text-[#8B7355]">Maße</dt>
                  <dd className="text-[#3D3D3D] text-right">
                    {[product.length, product.width, product.height].filter(Boolean).join(' × ')} cm
                  </dd>
                </div>
              )}
              {product.material && (
                <div className="flex justify-between gap-3">
                  <dt className="text-[#8B7355]">Material</dt>
                  <dd className="text-[#3D3D3D] text-right">{product.material}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* Recommendation-Sections (Container mit gleichem Padding wie Haupt-Card) */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* Perfektes Pärchen — 1 KI-kuratierter Ergänzungs-Artikel */}
      {pairProduct && (
        <div
          className="mt-12 border-t pt-8"
          data-testid="pair-with-section"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#D4A5A5] text-white text-[10px] tracking-[2px] uppercase">
              💝 Gift-Duo
            </span>
          </div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Georgia, serif' }}>
            {pairTagline || 'Perfektes Pärchen'}
          </h2>
          <p className="text-xs text-[#8B7355] mb-6">
            Unsere KI wählt den einen Artikel, der zu diesem Fund das schönste Duo bildet
          </p>
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch bg-gradient-to-br from-[#FAF5EE] to-[#F5EDE4] p-4 md:p-6 border border-[#E8DCC8]"
          >
            {/* Aktuelles Produkt (Referenz) */}
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 flex-shrink-0 border border-[#E8DCC8] bg-white overflow-hidden">
                {product?.images?.[0] && (
                  <img src={imgVariant(product.images[0], { w: 160 })} alt={product.name} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-[#8B7355] mb-1">Dieser Artikel</p>
                <p className="font-medium text-sm text-[#5D4E3C] line-clamp-2">{product?.name}</p>
                <p className="text-sm font-bold text-[#5D4E3C] mt-1">{formatEUR(Number(product?.price || 0))}</p>
              </div>
            </div>
            {/* Pärchen-Vorschlag */}
            <div className="flex items-center gap-4">
              <span className="text-3xl text-[#D4A5A5] flex-shrink-0" aria-hidden="true">+</span>
              <div
                className="flex-1 flex items-center gap-4 cursor-pointer group"
                onClick={() => navigate(`/products/${pairProduct.id}`)}
                data-testid="pair-with-product"
              >
                <div className="w-24 h-24 flex-shrink-0 border border-[#E8DCC8] bg-white overflow-hidden">
                  {pairProduct.images?.[0] && (
                    <img
                      src={imgVariant(pairProduct.images[0], { w: 400 })}
                      alt={pairProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-[#B0512E] mb-1">💝 Pärchen-Idee</p>
                  <p className="font-medium text-sm text-[#5D4E3C] line-clamp-2 group-hover:underline">{pairProduct.name}</p>
                  <p className="text-sm font-bold text-[#5D4E3C] mt-1">{formatEUR(Number(pairProduct.price || 0))}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                if (!addItem(pairProduct, 1, null, pairProduct.sale_price || pairProduct.price)) return;
                toast.success(`„${pairProduct.name}" wurde ergänzt`);
              }}
              className="bg-[#D4A5A5] hover:bg-[#B88585] text-white text-sm px-4 py-2 transition-colors"
              data-testid="pair-with-add-btn"
            >
              + Pärchen ergänzen
            </button>
          </div>
        </div>
      )}

      {/* Häufig zusammen gekauft — Collaborative Filtering */}
      {fbtProducts.length > 0 && (
        <div className="mt-12 border-t pt-8" data-testid="fbt-section">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#8B9B7E] text-white text-[10px] tracking-[2px] uppercase">
              🛒 {fbtSource === 'collaborative' ? 'Aus echten Bestellungen' : 'Passt gut dazu'}
            </span>
          </div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Georgia, serif' }}>
            {fbtTagline || 'Häufig zusammen gekauft'}
          </h2>
          <p className="text-xs text-[#8B7355] mb-6">
            {fbtSource === 'collaborative'
              ? 'Diese Artikel landen oft gemeinsam im Warenkorb unserer Kund:innen'
              : 'Diese Artikel wählen unsere Kund:innen gerne dazu'}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {fbtProducts.map((p) => (
              <div
                key={p.id}
                className="bg-white overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/products/${p.id}`)}
                data-testid={`fbt-product-${p.id}`}
              >
                <div className="aspect-square relative overflow-hidden bg-gray-100">
                  {p.images?.[0] ? (
                    <img
                      src={imgVariant(p.images[0], { w: 400 })}
                      alt={p.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">Kein Bild</div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-xs line-clamp-2 mb-1 min-h-[2rem]">{p.name}</h3>
                  <span className="inline-block text-white text-xs font-bold px-2 py-0.5" style={{ backgroundColor: '#C4A084' }}>
                    {formatEUR(Number(p.price || 0))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI-Curated Recommendations */}
      {relatedProducts.length > 0 && (
        <div className="mt-12 border-t pt-8" data-testid="ai-recommendations-section">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#C4A084] text-white text-[10px] tracking-[2px] uppercase">
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 0l3 9 9 3-9 3-3 9-3-9-9-3 9-3z" />
              </svg>
              KI-Auswahl
            </span>
          </div>
          <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Georgia, serif' }}>
            {recoTagline}
          </h2>
          <p className="text-xs text-[#8B7355] mb-6">
            Automatisch von unserer KI für dich kuratiert
          </p>
          <div className="relative group">
            {/* Left Arrow */}
            {relatedProducts.length > 4 && (
              <button
                onClick={() => {
                  const container = document.getElementById('related-products-scroll');
                  container.scrollBy({ left: -300, behavior: 'smooth' });
                }}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity -ml-4"
                aria-label="Nach links scrollen"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            {/* Scrollable Container */}
            <div
              id="related-products-scroll"
              className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {relatedProducts.map((relatedProduct) => (
                <div
                  key={relatedProduct.id}
                  className="flex-none w-64 bg-white overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/products/${relatedProduct.id}`)}
                >
                  <div className="aspect-square relative overflow-hidden">
                    {relatedProduct.images && relatedProduct.images.length > 0 ? (
                      <img
                        src={imgVariant(relatedProduct.images[0], { w: 400 })}
                        alt={relatedProduct.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <span className="text-gray-400">Kein Bild</span>
                      </div>
                    )}
                    {!(relatedProduct.stock > 0) && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <span className="text-white font-semibold">Ausverkauft</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-sm line-clamp-2 mb-2">
                      {relatedProduct.name}
                    </h3>
                    <span className="inline-block text-white font-bold px-3 py-1" style={{ backgroundColor: '#C4A084' }}>
                      {formatEUR(relatedProduct.price)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Right Arrow */}
            {relatedProducts.length > 4 && (
              <button
                onClick={() => {
                  const container = document.getElementById('related-products-scroll');
                  container.scrollBy({ left: 300, behavior: 'smooth' });
                }}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity -mr-4"
                aria-label="Nach rechts scrollen"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      </div>
      {/* /Recommendation-Container */}
      
      {/* Notepad Dialog */}
      <AddToNotepadDialog
        open={notepadDialog.open}
        onOpenChange={(open) => setNotepadDialog({ open, product: null })}
        product={notepadDialog.product}
        onAdd={handleAddToNotepadConfirm}
      />
    </div>
  );
};

export default ProductDetail;